/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Capacitor } from "@capacitor/core";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import DataController from "@controllers/DataController";
import HeaderTitle from "@components/common/HeaderTitle";
import ContentContainer from "@components/common/ContentContainer";
import { useEffect, useRef, useState } from "react";
import { IonSegmentCustomEvent } from "@ionic/core";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { evidenceFiltersAtom, EvidenceSearchFilter } from "@state/FilterState";
import { initialFilenameSelector } from "@state/DownloadState";
import { format } from "date-fns";
import classNames from "classnames";
import { Icon_Radio_Active, Icon_Radio_Grey } from "@assets/images";
import { AllProgressCheckStatuses, IAppFeatures, IEvidence, IUser, UsageData } from "@interfaces/Interfaces";
import * as EvidenceUtils from "@utils/evidenceUtils";
import WriteBlob from "capacitor-blob-writer";
import { Directory } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import { saveAs } from "file-saver";
import { pdf } from "@react-pdf/renderer";
import _ from "lodash";
import ExportedPDF from "@components/pdfExport/ExportedPDF";
import { appFeaturesAtom, evidenceAtom, progressCheckDataAtom, userAtom } from "@state/State";
import { useProgramData } from "@hooks/data/useProgramData";
import { PF_CONFIG_SERVER_URL } from "../../Constants";
import RemoteDataController from "@controllers/RemoteDataController";
import * as ProgramUtils from "@utils/programUtils";
import { PMUsageData, ProgramModule } from "pf-support-lib";
import { userRolesSelector } from "@state/UserState";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";

type Props = {
  evidence: IEvidence[];
  visible: boolean;
  closeModal: () => void;
};

enum SegmentOptions {
  GENERAL = "GENERAL",
  PROGRAM = "PROGRAM",
}

type DownloadOption = {
  option: string;
  label: string;
};

const DOWNLOAD_OPTIONS = [
  {
    option: "PDF",
    label: "Combined Evidence",
    subLabel: "A single PDF of all your evidence entries, with links to any attachments",
  },
  { option: "CSV", label: "Evidence List", subLabel: "A spreadsheet list of your selected evidence" },
  {
    option: "ZIP",
    label: "Full Profile",
    subLabel: `- A spreadsheet list of your evidence
- Each entry saved as a separate PDF
- Any files you attached (certificates, images, documents)
- Any written text saved as plain files (personal statement, summary of work)`,
  },
];

const CSV_ROWS = [
  "title",
  "activity",
  "numberOfHours",
  "date",
  "activityDate",
  "draft",
  // "standardTags",
  // "customTags",
  // "activityDescription",
  // "benefits",
  // "reflections",
  // "objectives",
  // "addToHCPCAudit"
];

const EvidenceDownloadModal = (props: Props) => {
  const filenameRef = useRef<HTMLInputElement>(null);

  const { userPrograms } = useProgramData();
  const allUserRoles = useRecoilValue(userRolesSelector);
  const { getCycleForProgram } = useProgramCycles();

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const userName = useRecoilValueLoadable<string>(initialFilenameSelector);
  const appFeatures = useRecoilValue<IAppFeatures[] | string[]>(appFeaturesAtom);

  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[]>(progressCheckDataAtom);

  const [segment, setSegment] = useState<string>(SegmentOptions.GENERAL);
  const [downloadOption, setDownloadOption] = useState<DownloadOption>(DOWNLOAD_OPTIONS[0]);
  const [downloading, setDownloading] = useState<boolean>(false);

  const [selectedProgram, setSelectedProgram] = useState<{ name: string; id: string } | null>(null);
  const [filename, setFilename] = useState<string>("");

  useEffect(() => {
    if (userName.state === "hasValue") {
      setFilename(`${userName.contents ?? "Untitled"}-Portfolio-${format(new Date(), "dd-MM-yyyy")}`);
    } else if (userName.state === "loading") {
      setFilename(`"Untitled-Portfolio-${format(new Date(), "dd-MM-yyyy")}`);
    }
  }, [userName.state]);

  const filters = useRecoilValue<EvidenceSearchFilter[]>(evidenceFiltersAtom);

  useEffect(() => {
    setDownloadOption(DOWNLOAD_OPTIONS[0]);
    setSelectedProgram(null);
    setFilename(`${userName.contents ?? "Untitled"}-Portfolio-${format(new Date(), "dd-MM-yyyy")}`);
  }, [segment]);

  const closeModal = () => {
    setDownloadOption(DOWNLOAD_OPTIONS[0]);
    setSelectedProgram(null);
    setFilename(`${userName.contents ?? "Untitled"}-Portfolio-${format(new Date(), "dd-MM-yyyy")}`);
    setSegment(SegmentOptions.GENERAL);
    props.closeModal();
  };

  const changeSegment = (event: IonSegmentCustomEvent<any>): void => {
    setSegment(event.detail.value);
  };

  const downloadPDF = async (): Promise<void> => {
    try {
      const doc = pdf();
      let pdfGenerated = false;

      const evidenceToExport = _.sortBy(structuredClone(props.evidence), (item) =>
        new Date(item.date).getTime()
      ).reverse();

      doc.updateContainer(
        <ExportedPDF
          evidence={evidenceToExport}
          user={user.contents}
          allUserRoles={allUserRoles}
          userPrograms={userPrograms}
        />
      );

      doc.on("change", async () => {
        try {
          if (!pdfGenerated && doc.container.document && doc.container.document.children.length > 1) {
            pdfGenerated = true;
            const blob = await doc.toBlob();

            if (await DataController.isWebAsync()) {
              saveAs(blob, filename.trim().length > 0 ? filename.trim() : "Untitled");
            } else {
              const filePath = await WriteBlob({
                path: `ParaFolio/Exports/Evidence/${filename.trim().length > 0 ? filename.trim() : "Untitled"}.pdf`,
                blob,
                directory: Directory.Library,
                recursive: true,
              });

              await FileOpener.open({
                filePath,
                contentType: "application/pdf",
                openWithDefault: false,
              });
            }

            setDownloading(false);
            doc.updateContainer(<></>);
          }
        } catch (error) {
          console.error(error);
          window.alert(
            "Failed to download combined evidence. For help please contact Customer Support at apps@class.co.uk"
          );
          setDownloading(false);
        }
      });
    } catch (error) {
      console.error(error);
      window.alert(
        "Failed to download combined evidence. For help please contact Customer Support at apps@class.co.uk"
      );
      setDownloading(false);
    }
  };

  const downloadProgramPDF = async (): Promise<void> => {
    try {
      const doc = pdf();

      let evidenceToExport = _.sortBy(structuredClone(allEvidence.contents), (item) =>
        new Date(item.date).getTime()
      ).reverse();
      let usageData: UsageData[] | undefined;

      if (selectedProgram?.id === "SWAST_MIC") {
        evidenceToExport = evidenceToExport.filter(
          (item: IEvidence) => item.standardTags && item.standardTags.length > 0
        );
      } else {
        evidenceToExport = evidenceToExport.filter((item: IEvidence) => {
          const evidenceJSON = item.evidenceJSON;
          const programName = evidenceJSON?.programInfo?.program || "";
          const programID = evidenceJSON?.programInfo?.programID || "";

          return programName === selectedProgram?.name || programID === selectedProgram?.id;
        });
      }

      evidenceToExport = evidenceToExport.filter((item: IEvidence) => {
        const onHoldReset = item.evidenceJSON?.OnHoldReset ?? 0;

        return onHoldReset !== 1 && item.draft !== true && item.private !== true;
      });

      const currentProgramCycle = selectedProgram?.id ? getCycleForProgram(selectedProgram?.id) : undefined;

      if (currentProgramCycle) {
        evidenceToExport = evidenceToExport.filter((item: IEvidence) => {
          return item.evidenceJSON?.programInfo?.programCycle === currentProgramCycle;
        });
      }

      const program = userPrograms.find((program) => program.ID === selectedProgram?.id);

      if (program && ProgramUtils.shouldCalculateUsageData(program.ID)) {
        const pm = new ProgramModule(program);
        const contactID = Number(user.contents.userData.contactID);
        const roleID = ProgramUtils.getSelectedRoleIdForEPRRProgram(user.contents, program.ID);
        const pMUsageData = new PMUsageData(pm, []);

        usageData = pMUsageData.getUsageDataByUser(pm, contactID, roleID, evidenceToExport);
      }

      let programProgressCheckData: AllProgressCheckStatuses | null = null;

      if (allProgressCheckData.state === "hasValue" && program) {
        const dataForProgram = allProgressCheckData.contents?.find(
          (item: any): item is AllProgressCheckStatuses => item.programID === program.ID
        );

        if (dataForProgram) {
          programProgressCheckData = dataForProgram;
        }
      }

      let pdfGenerated = false;

      const element = (
        <ExportedPDF
          evidence={evidenceToExport}
          user={user.contents}
          allUserRoles={allUserRoles}
          isMajorIncident={selectedProgram?.id === "SWAST_MIC"}
          program={program}
          userPrograms={userPrograms}
          usageData={usageData}
          programProgressCheckData={programProgressCheckData}
        />
      );

      doc.updateContainer(element);

      doc.on("change", async () => {
        try {
          if (!pdfGenerated && doc.container.document && doc.container.document.children.length > 1) {
            pdfGenerated = true;

            const blob = await doc.toBlob();

            if (await DataController.isWebAsync()) {
              saveAs(blob, filename.trim().length > 0 ? filename.trim() : "Untitled");
            } else {
              const filePath = await WriteBlob({
                path: `ParaFolio/Exports/Evidence/${filename.trim().length > 0 ? filename.trim() : "Untitled"}.pdf`,
                blob,
                directory: Directory.Library,
                recursive: true,
              });

              await FileOpener.open({
                filePath,
                contentType: "application/pdf",
                openWithDefault: false,
              });
            }

            setDownloading(false);
            doc.updateContainer(<></>);
          }
        } catch (error) {
          console.error(error);
          window.alert(
            "Failed to download program portfolio. For help please contact Customer Support at apps@class.co.uk"
          );
          setDownloading(false);
        }
      });
    } catch (error) {
      console.error(error);
      window.alert(
        "Failed to download program portfolio. For help please contact Customer Support at apps@class.co.uk"
      );
      setDownloading(false);
    }
  };

  const downloadCSV = async (): Promise<void> => {
    const csv = EvidenceUtils.generateCSV(props.evidence, CSV_ROWS);

    const blob = new Blob([csv], { type: "text/csv" });

    if (await DataController.isWebAsync()) {
      saveAs(blob, filename.trim().length > 0 ? filename.trim() : "Untitled");
    } else {
      const filePath = await WriteBlob({
        path: `ParaFolio/Exports/CSV/${filename.trim().length > 0 ? filename.trim() : "Untitled"}.csv`,
        blob,
        directory: Directory.Library,
        recursive: true,
      });

      await FileOpener.open({
        filePath,
        contentType: "text/csv",
        openWithDefault: false,
      });
    }

    setDownloading(false);
  };

  const downloadAuditProfile = async (): Promise<void> => {
    const evidenceIds = props.evidence.map((item) => item.id);

    const url = PF_CONFIG_SERVER_URL + "/pdf/generate-audit-profile";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({
          contactID: `${user.contents?.userData?.contactID}`,
          token: user.contents?.token,
          evidenceIds,
        }),
      });

      if (response.status === 200) {
        const blob = await response.blob();

        if (await DataController.isWebAsync()) {
          saveAs(blob, filename.trim().length > 0 ? filename.trim() : "Untitled");
        } else {
          const filePath = await WriteBlob({
            path: `ParaFolio/Exports/AuditProfile/${filename.trim().length > 0 ? filename.trim() : "Untitled"}.zip`,
            blob,
            directory: Directory.Library,
            recursive: true,
          });

          await FileOpener.open({
            filePath,
            contentType: "application/zip",
            openWithDefault: false,
          });
        }
      } else {
        window.alert("Failed to download full profile. For help please contact Customer Support at apps@class.co.uk");
      }

      setDownloading(false);
    } catch (error: any) {
      console.error(error);
      RemoteDataController.logError(error);
      setDownloading(false);
    }
  };

  const downloadPortfolio = async (): Promise<void> => {
    setDownloading(true);

    if (segment === SegmentOptions.GENERAL) {
      try {
        if (downloadOption.option === "PDF") {
          await downloadPDF();
        } else if (downloadOption.option === "CSV") {
          await downloadCSV();
        } else if (downloadOption.option === "ZIP") {
          await downloadAuditProfile();
        }
      } catch (error) {
        console.error(error);
      }
    } else if (segment === SegmentOptions.PROGRAM) {
      try {
        downloadProgramPDF();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const filenameChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFilename(event.target.value);
  };

  const selectionText = EvidenceUtils.getDownloadSelectionText(filters, props.evidence);

  return (
    <IonModal
      id="evidence-download-modal"
      isOpen={props.visible}
      backdropDismiss
      canDismiss
      className="fullscreen"
      onDidDismiss={() => closeModal()}
    >
      <IonHeader
        style={{
          height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
        }}
      >
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{ maxWidth: DataController.isWeb() ? 980 : undefined }}
        >
          <IonButtons slot="start">
            <IonButton
              mode="ios"
              className="cancelButton"
              onClick={props.closeModal}
            >
              {"Close"}
            </IonButton>
          </IonButtons>
          <HeaderTitle>{"Download Portfolio"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <ContentContainer className="!pt-3 pb-32">
          <IonSegment
            className="segmentContainer"
            mode="ios"
            value={segment}
            onIonChange={(event) => changeSegment(event)}
          >
            <IonSegmentButton value={SegmentOptions.GENERAL}>
              <IonLabel className="py-2.5">{"Selection"}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value={SegmentOptions.PROGRAM}>
              <IonLabel className="py-2.5">{"Program modules"}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
          {segment === SegmentOptions.GENERAL && (
            <>
              <div className="mt-5">
                <div className="text-acc-13px font-bold text-neutral-500">{"Your selection"}</div>
                <div className="text-acc-16px leading-acc-lh font-acc-500 text-grey-90 mt-1">{selectionText}</div>
              </div>
              <div className="mt-5">
                <div className="text-acc-13px font-bold text-neutral-500">{"Filename"}</div>
                <input
                  ref={filenameRef}
                  className="h-12.5 w-full rounded-lg border-solid border-half border-grey-50 bg-grey-10 px-3 text-16px font-acc-normal leading-6 tracking-default text-grey-90 mt-2"
                  placeholder="Enter a filename..."
                  autoComplete="off"
                  autoCapitalize="words"
                  id="evidence-download-input"
                  value={filename}
                  onChange={filenameChanged}
                />
              </div>
              <div className="mt-5">
                <div className="text-acc-13px font-bold text-neutral-500 mb-2">{"Download options"}</div>
                {DOWNLOAD_OPTIONS.map((option) => {
                  const isSelected = downloadOption.option === option.option;

                  return (
                    <IonItem
                      button
                      detail={false}
                      className="ion-item-no-padding"
                      disabled={downloading}
                      onClick={() => setDownloadOption(option)}
                      key={option.option}
                    >
                      <div className="min-h-13 flex flex-row items-center justify-between w-full">
                        <div className="flex flex-col py-3">
                          <div
                            className={classNames("text-acc-16px text-grey-90", {
                              "font-acc-600 tracking-normal": isSelected,
                            })}
                          >
                            {option.label}
                          </div>
                          <div className="text-acc-13px text-zinc-600 whitespace-pre-wrap mt-2">{option.subLabel}</div>
                        </div>
                        <img
                          src={isSelected ? Icon_Radio_Active : Icon_Radio_Grey}
                          className="size-11 img-no-select -mr-2"
                        />
                      </div>
                    </IonItem>
                  );
                })}
              </div>
            </>
          )}
          {segment === SegmentOptions.PROGRAM && (
            <>
              <div className="mt-5">
                <div className="text-acc-13px font-bold text-neutral-500 mb-2">{"Program modules"}</div>
                {appFeatures.includes(IAppFeatures.SWAST_MIC) && (
                  <IonItem
                    button
                    detail={false}
                    className="ion-item-no-padding"
                    disabled={downloading}
                    onClick={() =>
                      setSelectedProgram((prev) =>
                        prev?.id === "SWAST_MIC" ? null : { name: "SWAST_MIC", id: "SWAST_MIC" }
                      )
                    }
                  >
                    <div className="min-h-13 flex flex-row items-center justify-between w-full">
                      <div className="flex flex-col py-3">
                        <div
                          className={classNames("text-acc-16px text-grey-90", {
                            "font-acc-600 tracking-normal": selectedProgram?.id === "SWAST_MIC",
                          })}
                        >
                          {"Incident Portfolio"}
                        </div>
                      </div>
                      <img
                        src={selectedProgram?.id === "SWAST_MIC" ? Icon_Radio_Active : Icon_Radio_Grey}
                        className="size-11 img-no-select -mr-2"
                      />
                    </div>
                  </IonItem>
                )}
                {userPrograms.map((program) => {
                  const selected = selectedProgram?.id === program.ID;

                  return (
                    <IonItem
                      button
                      detail={false}
                      className="ion-item-no-padding"
                      disabled={downloading}
                      onClick={() =>
                        setSelectedProgram((prev) =>
                          prev?.id === program.ID ? null : { name: program.Name, id: program.ID }
                        )
                      }
                      key={program.ID}
                    >
                      <div className="min-h-13 flex flex-row items-center justify-between w-full">
                        <div className="flex flex-col py-3">
                          <div
                            className={classNames("text-acc-16px text-grey-90", {
                              "font-acc-600 tracking-normal": selected,
                            })}
                          >
                            {program.Title}
                          </div>
                        </div>
                        <img
                          src={selected ? Icon_Radio_Active : Icon_Radio_Grey}
                          className="size-11 img-no-select -mr-2"
                        />
                      </div>
                    </IonItem>
                  );
                })}
              </div>
              <div className="mt-5"></div>
            </>
          )}
        </ContentContainer>
      </IonContent>
      <div className="flex justify-center bg-white absolute bottom-0 w-full !z-[999999 pb-safe-area-footer">
        <div className="flex items-center h-20 justify-center w-full max-w-app-column pl-safe-area-l pr-safe-area-r">
          <IonButton
            mode="ios"
            disabled={downloading || (segment === SegmentOptions.PROGRAM && !selectedProgram)}
            className={`flex w-full max-w-button mb-12px self-center`}
            onClick={() => downloadPortfolio()}
          >
            {downloading ? (
              <IonSpinner className="size-6 text-white" />
            ) : (
              <div className="text-white button text-default leading-default text-center tracking-default font-bold">
                {"Download"}
              </div>
            )}
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

export default EvidenceDownloadModal;
